import { useCallback, useEffect, useRef, useState } from 'react'
import { errorMessage } from '../services/api'

export interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
}

/**
 * Runs an async loader on mount and whenever `deps` change.
 *
 * @param loader - Data fetcher; must be stable or listed in `deps`.
 * @param deps - Dependency list, same semantics as `useEffect`.
 *
 * @remarks
 * Results from a superseded run are discarded, so rapid filter changes cannot
 * render stale data.
 *
 * @example
 * ```typescript
 * const { data, loading } = useAsync(() => fetchStandings(season), [season])
 * ```
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)
  const runIdRef = useRef(0)

  const loaderRef = useRef(loader)
  loaderRef.current = loader

  useEffect(() => {
    const runId = ++runIdRef.current
    setLoading(true)
    setError(null)

    loaderRef
      .current()
      .then(result => {
        if (runIdRef.current === runId) {
          setData(result)
        }
      })
      .catch(caught => {
        if (runIdRef.current === runId) {
          setError(errorMessage(caught))
        }
      })
      .finally(() => {
        if (runIdRef.current === runId) {
          setLoading(false)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce(value => value + 1), [])

  return { data, loading, error, reload }
}
